/**
 * Release Order Detail Page
 * Shows release order details, status history, and allows state machine transitions.
 * Handles version field for optimistic locking.
 * 
 * State machine: drafted → reviewing → approved / rejected → released
 */
import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { useReleaseOrderDetail, useReleaseOrderTransition, type ReleaseOrderView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { canReadReleaseOrder, canUpdateReleaseOrder } from '@/lib/labPermissions';

type ReleaseOrderStatus = 'pending' | 'approved' | 'rejected' | 'recalled';

// Valid state transitions per state-machines.md
// Release Orders: drafted → reviewing → approved / rejected → released
const VALID_TRANSITIONS: Record<string, string[]> = {
  pending: ['approved', 'rejected', 'recalled'],
  approved: ['released', 'recalled'],
  rejected: ['recalled'],
  recalled: [],
  released: [],
};

export default function ReleaseOrderDetailPage() {
  const { releaseOrderId } = useParams<{ releaseOrderId: string }>();
  const { permissions } = useAuth();
  const { fetch: fetchDetail, loading: detailLoading } = useReleaseOrderDetail(releaseOrderId!);
  const { transition: transitionStatus, loading: transitionLoading } = useReleaseOrderTransition(releaseOrderId!);

  const [order, setOrder] = useState<ReleaseOrderView | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<ReleaseOrderStatus | ''>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadReleaseOrder(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看放行单的权限</p>
        </Card>
      </div>
    );
  }

  // Load release order detail
  useEffect(() => {
    const loadOrder = async () => {
      try {
        const data = await fetchDetail();
        if (data) {
          setOrder(data);
        }
      } catch (err) {
        toast.error('加载放行单详情失败');
      }
    };

    if (releaseOrderId) {
      loadOrder();
    }
  }, [releaseOrderId, fetchDetail]);

  const handleTransition = async () => {
    if (!order || !selectedTransition) {
      toast.error('请选择转换状态');
      return;
    }

    if (!canUpdateReleaseOrder(permissions)) {
      toast.error('您没有修改放行单状态的权限');
      return;
    }

    try {
      await transitionStatus(selectedTransition, order.version);
      toast.success('状态已更新');
      setSelectedTransition('');
      setConfirmDialogOpen(false);
      
      // Refetch order details to ensure consistency with backend
      const updatedData = await fetchDetail();
      if (updatedData) {
        setOrder(updatedData);
      }
    } catch (err: unknown) {
      // Backend rejection must be shown as-is
      toast.error(`状态转换失败: ${(err as Error)?.message || '未知错误'}`);
    }
  };

  if (detailLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">加载中…</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">放行单不存在</h2>
        </Card>
      </div>
    );
  }

  const validTransitions = VALID_TRANSITIONS[order.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">放行单详情</h1>
        <p className="text-sm text-muted-foreground">放行单 ID: {order.id}</p>
      </div>

      {/* Release Order Info */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-muted-foreground">批次 ID</label>
            <p className="mt-1 text-sm font-mono">{order.cellBatchId}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">当前状态</label>
            <p className="mt-1 text-sm">
              <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-900">
                {getStatusLabel(order.status)}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">放行单号</label>
            <p className="mt-1 text-sm font-mono">{order.releaseNo}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">创建时间</label>
            <p className="mt-1 text-sm">{new Date(order.createdAt).toLocaleString()}</p>
          </div>
        </div>
      </Card>

      {/* Status Transition */}
      {validTransitions.length > 0 && canUpdateReleaseOrder(permissions) && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">状态转换</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">选择目标状态</label>
              <select
                value={selectedTransition}
                onChange={(e) => setSelectedTransition((e.target.value || '') as ReleaseOrderStatus | '')}
                className="mt-2 w-full rounded border border-input bg-background px-3 py-2 text-sm"
                disabled={transitionLoading}
              >
                <option value="">-- 选择状态 --</option>
                {validTransitions.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!selectedTransition || transitionLoading}
              className="w-full"
            >
              {transitionLoading ? '处理中…' : '确认转换'}
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog (simple version) */}
      {confirmDialogOpen && (
        <Card className="border-yellow-200 bg-yellow-50 p-6">
          <h4 className="font-semibold text-yellow-900">确认状态转换</h4>
          <p className="mt-2 text-sm text-yellow-800">
            您确定要将放行单从 <strong>{getStatusLabel(order.status)}</strong> 转换到{' '}
            <strong>{getStatusLabel(selectedTransition)}</strong> 吗？
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleTransition}
              disabled={transitionLoading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {transitionLoading ? '处理中…' : '确认'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedTransition('');
              }}
              disabled={transitionLoading}
            >
              取消
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: '待审批',
    approved: '已批准',
    rejected: '已拒绝',
    recalled: '已撤回',
    released: '已放行',
  };
  return labels[status] || status;
}
